/**
 * Aerware Foundry Dashboard
 *
 * Main application component with routing.
 */

import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { useOnboardingStore } from '@/stores/onboarding';
import { useAlishaChatStore } from '@/stores/alishaChat';
import { Layout } from '@/components/layout';
import { ScrollToTop } from '@/components/ScrollToTop';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { RequireStage } from '@/components/RequireStage';
import { PAGE_STAGE_REQUIREMENTS } from '@/stores/subscription';

// Lazy-loaded page components (route-level code splitting)
const CommandCenter = lazy(() => import('@/pages/CommandCenter').then(m => ({ default: m.CommandCenter })));
const Exchange = lazy(() => import('@/pages/Exchange').then(m => ({ default: m.Exchange })));
const Watchtower = lazy(() => import('@/pages/Watchtower').then(m => ({ default: m.Watchtower })));
const TaskDetail = lazy(() => import('@/pages/TaskDetail').then(m => ({ default: m.TaskDetail })));
const Tasks = lazy(() => import('@/pages/Tasks').then(m => ({ default: m.Tasks })));
const Workforce = lazy(() => import('@/pages/Workforce').then(m => ({ default: m.Workforce })));
const TeamDetail = lazy(() => import('@/pages/TeamDetail').then(m => ({ default: m.TeamDetail })));
const OperatorProfile = lazy(() => import('@/pages/OperatorProfile').then(m => ({ default: m.OperatorProfile })));
const OperatorTemplates = lazy(() => import('@/pages/OperatorTemplates').then(m => ({ default: m.OperatorTemplates })));
const CompanyCore = lazy(() => import('@/pages/CompanyCore').then(m => ({ default: m.CompanyCore })));
const Artifacts = lazy(() => import('@/pages/Artifacts').then(m => ({ default: m.Artifacts })));
const Marketplace = lazy(() => import('@/pages/Marketplace').then(m => ({ default: m.Marketplace })));
const KnowledgeBase = lazy(() => import('@/pages/KnowledgeBase').then(m => ({ default: m.KnowledgeBase })));
const Roadmap = lazy(() => import('@/pages/Roadmap').then(m => ({ default: m.Roadmap })));
const Goals = lazy(() => import('@/pages/Goals').then(m => ({ default: m.Goals })));
const Customers = lazy(() => import('@/pages/Customers').then(m => ({ default: m.Customers })));
const CreditHistory = lazy(() => import('@/pages/CreditHistory').then(m => ({ default: m.CreditHistory })));
const Login = lazy(() => import('@/pages/Login').then(m => ({ default: m.Login })));
const Signup = lazy(() => import('@/pages/Signup').then(m => ({ default: m.Signup })));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword').then(m => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import('@/pages/ResetPassword').then(m => ({ default: m.ResetPassword })));
const Settings = lazy(() => import('@/pages/Settings').then(m => ({ default: m.Settings })));
const Pricing = lazy(() => import('@/pages/Pricing').then(m => ({ default: m.Pricing })));
const VerifyEmail = lazy(() => import('@/pages/VerifyEmail').then(m => ({ default: m.VerifyEmail })));
const OAuthCallback = lazy(() => import('@/pages/OAuthCallback').then(m => ({ default: m.OAuthCallback })));
const OrgChart = lazy(() => import('@/pages/OrgChart').then(m => ({ default: m.OrgChart })));
const Skills = lazy(() => import('@/pages/Skills').then(m => ({ default: m.Skills })));
const Swarms = lazy(() => import('@/pages/Swarms').then(m => ({ default: m.Swarms })));
const Spending = lazy(() => import('@/pages/Spending').then(m => ({ default: m.default })));
const Help = lazy(() => import('@/pages/Help').then(m => ({ default: m.Help })));

// Onboarding pages
const Welcome = lazy(() => import('@/pages/onboarding/Welcome').then(m => ({ default: m.Welcome })));
const WelcomeBack = lazy(() => import('@/pages/onboarding/WelcomeBack').then(m => ({ default: m.WelcomeBack })));
const PathSelection = lazy(() => import('@/pages/onboarding/PathSelection').then(m => ({ default: m.PathSelection })));
const OnboardingChat = lazy(() => import('@/pages/onboarding/OnboardingChat').then(m => ({ default: m.OnboardingChat })));
const PathBQualifier = lazy(() => import('@/pages/onboarding/PathBQualifier').then(m => ({ default: m.PathBQualifier })));
const PathAReview = lazy(() => import('@/pages/onboarding/PathAReview').then(m => ({ default: m.PathAReview })));
const PathASummary = lazy(() => import('@/pages/onboarding/PathASummary').then(m => ({ default: m.PathASummary })));
const PathBResults = lazy(() => import('@/pages/onboarding/PathBResults').then(m => ({ default: m.PathBResults })));

/** Helper to wrap a page with stage-gating */
function Gated({ path, children }: { path: string; children: React.ReactNode }) {
  const req = PAGE_STAGE_REQUIREMENTS[path];
  if (!req) return <>{children}</>;
  return (
    <RequireStage
      stage={req.stage}
      featureName={req.featureName}
      description={req.description}
      capabilities={req.capabilities}
    >
      {children}
    </RequireStage>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

/** Redirects to onboarding if not yet completed */
function OnboardingGuard({ children }: { children: React.ReactNode }) {
  const { completedAt, step, path, hasConversation, pathAOutputs, pathBOutputs } = useOnboardingStore();

  if (!completedAt) {
    // Smart routing based on where user left off

    // Priority 1: If assessment/outputs exist, go to review/summary pages
    if (step === 'summary') {
      return <Navigate to="/onboarding/summary" replace />;
    }

    // If outputs exist, route to review page (assessment has been generated)
    if (path === 'path_a' && pathAOutputs) {
      return <Navigate to="/onboarding/review" replace />;
    } else if (path === 'path_b' && pathBOutputs) {
      return <Navigate to="/onboarding/results" replace />;
    }

    // Priority 2: If user has selected a path but no outputs yet, route to chat/qualifier
    if (path) {
      // Check if user has existing conversation data
      if (hasConversation) {
        // Returning user with conversation - show welcome back screen
        return <Navigate to="/onboarding/welcome-back" replace />;
      }

      // Has path but no conversation messages yet
      if (path === 'path_a') {
        // Path A goes straight to chat
        return <Navigate to="/onboarding/chat" replace />;
      } else if (path === 'path_b') {
        // Path B: check if they're in qualifier or chat
        if (step === 'chat') {
          return <Navigate to="/onboarding/chat" replace />;
        }
        return <Navigate to="/onboarding/qualifier" replace />;
      }
    } else if (step === 'path_selection') {
      // User is on path selection screen
      return <Navigate to="/onboarding/path-selection" replace />;
    }
    // Fresh user - start from beginning
    return <Navigate to="/onboarding/welcome" replace />;
  }

  return <>{children}</>;
}

/** Redirects away from onboarding if already completed (prevents back-button re-entry) */
function CompletedOnboardingGuard({ children }: { children: React.ReactNode }) {
  const { completedAt } = useOnboardingStore();

  if (completedAt) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

/** Hydrates stores from backend when user authenticates */
function SessionHydrator({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  const [isHydrating, setIsHydrating] = useState(false);
  const hydrated = useRef(false);

  useEffect(() => {
    if (isAuthenticated && !hydrated.current) {
      hydrated.current = true;
      setIsHydrating(true);
      Promise.all([
        useOnboardingStore.getState().hydrateFromBackend(),
        useAlishaChatStore.getState().hydrateFromBackend(),
      ]).finally(() => {
        setIsHydrating(false);
      });
    }
    if (!isAuthenticated) {
      hydrated.current = false;
      setIsHydrating(false);
    }
  }, [isAuthenticated]);

  // Show loading while hydrating on first authentication
  if (isAuthenticated && isHydrating) {
    return (
      <div className="flex items-center justify-center h-screen" style={{ background: '#0B0F16' }}>
        <div className="animate-pulse text-[#8A8F98]">Loading...</div>
      </div>
    );
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <ErrorBoundary>
      <ScrollToTop />
      <SessionHydrator>
      <Suspense fallback={<div className="flex items-center justify-center h-screen" style={{ background: '#0B0F16' }}><div className="animate-pulse text-[#8A8F98]">Loading...</div></div>}>
      <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/oauth/callback" element={<OAuthCallback />} />

      {/* Onboarding routes (protected but no sidebar, guarded against re-entry) */}
      <Route
        path="/onboarding/welcome"
        element={
          <ProtectedRoute><CompletedOnboardingGuard>
            <Welcome />
          </CompletedOnboardingGuard></ProtectedRoute>
        }
      />
      <Route
        path="/onboarding/welcome-back"
        element={
          <ProtectedRoute><CompletedOnboardingGuard>
            <WelcomeBack />
          </CompletedOnboardingGuard></ProtectedRoute>
        }
      />
      <Route
        path="/onboarding/path-selection"
        element={
          <ProtectedRoute><CompletedOnboardingGuard>
            <PathSelection />
          </CompletedOnboardingGuard></ProtectedRoute>
        }
      />
      <Route
        path="/onboarding/chat"
        element={
          <ProtectedRoute><CompletedOnboardingGuard>
            <OnboardingChat />
          </CompletedOnboardingGuard></ProtectedRoute>
        }
      />
      <Route
        path="/onboarding/qualifier"
        element={
          <ProtectedRoute><CompletedOnboardingGuard>
            <PathBQualifier />
          </CompletedOnboardingGuard></ProtectedRoute>
        }
      />
      <Route
        path="/onboarding/review"
        element={
          <ProtectedRoute><CompletedOnboardingGuard>
            <PathAReview />
          </CompletedOnboardingGuard></ProtectedRoute>
        }
      />
      <Route
        path="/onboarding/summary"
        element={
          <ProtectedRoute><CompletedOnboardingGuard>
            <PathASummary />
          </CompletedOnboardingGuard></ProtectedRoute>
        }
      />
      <Route
        path="/onboarding/results"
        element={
          <ProtectedRoute><CompletedOnboardingGuard>
            <PathBResults />
          </CompletedOnboardingGuard></ProtectedRoute>
        }
      />

      {/* Protected routes (require auth + completed onboarding) */}
      <Route
        element={
          <ProtectedRoute>
            <OnboardingGuard>
              <Layout />
            </OnboardingGuard>
          </ProtectedRoute>
        }
      >
        {/* Main navigation (stage-gated) */}
        <Route index element={<Gated path="/command-center"><CommandCenter /></Gated>} />
        <Route path="command-center" element={<Gated path="/command-center"><CommandCenter /></Gated>} />
        <Route path="exchange" element={<Gated path="/exchange"><Exchange /></Gated>} />
        <Route path="watchtower" element={<Gated path="/watchtower"><Watchtower /></Gated>} />
        <Route path="watchtower/:taskId" element={<Gated path="/watchtower"><TaskDetail /></Gated>} />
        <Route path="tasks" element={<Gated path="/tasks"><Tasks /></Gated>} />
        <Route path="workforce" element={<Gated path="/workforce"><Workforce /></Gated>} />
        <Route path="workforce/team/:teamId" element={<Gated path="/workforce"><TeamDetail /></Gated>} />
        <Route path="workforce/:operatorId" element={<Gated path="/workforce"><OperatorProfile /></Gated>} />
        <Route path="templates" element={<Gated path="/templates"><OperatorTemplates /></Gated>} />
        <Route path="marketplace" element={<Gated path="/marketplace"><Marketplace /></Gated>} />
        <Route path="core" element={<Gated path="/core"><CompanyCore /></Gated>} />
        <Route path="skills" element={<Gated path="/core"><Skills /></Gated>} />
        <Route path="knowledge" element={<Gated path="/core"><KnowledgeBase /></Gated>} />
        <Route path="org-chart" element={<OrgChart />} />
        <Route path="roadmap" element={<Roadmap />} />
        <Route path="goals" element={<Goals />} />
        <Route path="customers" element={<Customers />} />
        <Route path="artifacts" element={<Artifacts />} />
        <Route path="swarms" element={<Gated path="/workforce"><Swarms /></Gated>} />
        <Route path="credit-history" element={<Gated path="/credit-history"><CreditHistory /></Gated>} />
        <Route path="spending" element={<Spending />} />
        <Route path="settings" element={<Settings />} />
        <Route path="settings/:section" element={<Settings />} />
        <Route path="pricing" element={<Pricing />} />
        <Route path="help" element={<Help />} />

        {/* Legacy routes - redirect to new paths */}
        <Route path="files" element={<Navigate to="/artifacts" replace />} />
        <Route path="operators" element={<Navigate to="/workforce" replace />} />
        <Route path="escalations" element={<Navigate to="/watchtower" replace />} />
        <Route path="iris" element={<Navigate to="/exchange" replace />} />
        <Route path="health" element={<Navigate to="/watchtower" replace />} />
        <Route path="acropolis" element={<Navigate to="/watchtower" replace />} />
        <Route path="agora" element={<Navigate to="/marketplace" replace />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
      </Suspense>
      </SessionHydrator>
    </ErrorBoundary>
  );
}
